import {Inject, Injectable} from '@angular/core';
import {DOCUMENT} from '@angular/common';
import {HttpClient} from "@angular/common/http";

import {AppAlertService} from "../app-alert.service";
import {ParametersService} from "../parameters.service";
import {
    SettingsNotificationsDefaultService
} from "./settings-notifications-default.service";
import {SubtitlesService} from "../subtitles.service";
import {UserService} from "../user.service";
import {
    AlarmType,
    AssignedAccount, DeletedAccount,
    Device,
    NotificationConfig,
    NotificationConfigElement, TimeType
} from "../../models";
import * as _ from "lodash";
import {SettingsService} from "./settings.service";


@Injectable({
    providedIn: 'root'
})
export class SettingsNotificationsService {
    accountToDelete: string = '';
    allPushDevices: Device[] | null = null;
    assignedAccounts: AssignedAccount[] | null = null;
    deviceToDelete: Device | null = null;
    private deviceToken: null | string = '';
    hoursDifference: number = 0;
    minutesDifference: number = 0;
    showNotificationDialog: AlarmType | 'changeEmail' | '' = '';
    time: NotificationConfigElement = {
        id: 0,
        from: {hour: 0, minute: 0, whole: ''},
        to: {hour: 0, minute: 0, whole: ''},
        enable: false
    }
    newNotificationEmail: string = '';

    constructor(
        @Inject(DOCUMENT) private document: Document,
        private appAlert: AppAlertService,
        private http: HttpClient,
        private parameters: ParametersService,
        private settings: SettingsService,
        private settingsNotificationsDefault: SettingsNotificationsDefaultService,
        private subtitles: SubtitlesService,
        private user: UserService,
    ) {
    }

    subtitle(val: number): string {
        return val !== 0 ? this.subtitles.setSubtitle(val) : '';
    }

    getAssignedAccounts(fromController?: boolean) {
        if ((fromController && this.assignedAccounts !== null) || this.parameters.demoAccount()) return;

        let deviceElement = this.document.getElementById('device_token');
        if (deviceElement) {
            this.deviceToken = deviceElement.innerHTML;
        }

        if (this.deviceToken === '') {
            deviceElement = this.document.getElementById('apple_token');
            if (deviceElement) {
                this.deviceToken = deviceElement.innerHTML;
            }
        }

        if (this.deviceToken) {
            const baseUrl = `/api/v1/users/${this.user.user_id}`;
            const token = this.deviceToken;
            const path = `${baseUrl}/push/notifications/assigned_accounts/${token}`;

            this.http.get<AssignedAccount[]>(path).subscribe((response) => {
                this.assignedAccounts = response;
            });
        }
    }

    removeAssignedAccount(): void {
        if (this.accountToDelete) {
            const baseUrl = `/api/v1/users/${this.user.user_id}`;
            const token = this.deviceToken;
            const hashId = this.accountToDelete
            const path = `${baseUrl}/notifications/push/assigned_accounts/${token}/${hashId}`;

            this.http.delete<DeletedAccount>(path).subscribe((response) => {
                if (response.index) {
                    const index = this.assignedAccounts.findIndex(
                        (account) => account.id === response.index);

                    if (index !== -1) {
                        this.assignedAccounts.splice(index, 1);
                    }
                    this.accountToDelete = '';
                }
            });
        }
    }

    getAllPushDevices(fromController?: boolean) {
        if ((fromController && this.allPushDevices !== null) || this.parameters.demoAccount()) return;

        const baseUrl = `/api/v1/users/${this.user.user_id}`;
        const path = `${baseUrl}/notifications/push/devices`

        this.http.get<Device[]>(path).subscribe(response => {
            this.allPushDevices = response;
        });
    }

    addNotificationDevice() {
        const element = this.document.getElementById('device_token')
        let deviceToken = element ? element.innerHTML : '';

        if (deviceToken === '') {
            const element = this.document.getElementById('apple_token');
            if (element) {
                deviceToken = element.innerHTML;
            }
        }

        if (deviceToken !== '') {
            const distribution = this.document.getElementById('distribution').innerHTML ?? '';
            const deviceName = this.document.getElementById('device_name').innerHTML ?? '';
            const platform = this.document.getElementById('platform').innerHTML ?? '';

            const data = {
                'device_token': deviceToken,
                distribution,
                device_name: deviceName,
                platform,
            }
            const baseUrl = `/api/v1/users/${this.user.user_id}`;

            this.http.post(`${baseUrl}/notifications/push/device`, data)
                .subscribe(() => {
                    this.getAllPushDevices();
                });
        }
    }

    deleteDevice(): void {
        if (!this.deviceToDelete) return;

        const baseUrl = `/api/v1/users/${this.user.user_id}`;
        this.http.delete(`${baseUrl}/notifications/push/devices/${this.deviceToDelete.id}`)
            .subscribe(() => {
                this.getAllPushDevices();
            });
    }

    setNotificationTime(udid: string | 0, notificationType: keyof NotificationConfig): void {
        switch (notificationType) {
            case "error":
                this.time = this.settingsNotificationsDefault.notificationConfig.getValue().error;
                break;
            case "warning":
                this.time = this.settingsNotificationsDefault.notificationConfig.getValue().warning;
                break;
            case "info":
                this.time = this.settingsNotificationsDefault.notificationConfig.getValue().info;
                break;
        }

        let start = (this.time.from.hour < 10 ? '0' : '')
            + this.time.from.hour + ':'
            + (this.time.from.minute < 10 ? '0' : '')
            + this.time.from.minute;
        let end = (this.time.to.hour < 10 ? '0' : '')
            + this.time.to.hour + ':'
            + (this.time.to.minute < 10 ? '0' : '')
            + this.time.to.minute;

        if (!this.time.enable) {
            start = '00:00';
            end = '00:00';
        }

        if (start === end && this.time.enable){
            this.appAlert.openAlertDialog({
                title: this.subtitle(5014),
                message: this.subtitle(16375),
                okButtonText: this.subtitle(475),
            });
        } else{
        const data = {
            data: {
                notification_type: notificationType,
                start,
                end,
                enable: this.time.enable,
                id: this.time.id
            }
        }

        const baseUrl = `/api/v1/users/${this.user.user_id}/modules/${udid}`;

        this.http.put(`${baseUrl}/notifications/time`, data)
            .subscribe(() => {
                this.settingsNotificationsDefault.getNotificationInformation(udid);
                this.diffHours();
                this.showNotificationDialog = '';
            });
        }
    }

    setDialogTime(type: keyof NotificationConfig): void {
        this.time = this.settingsNotificationsDefault.notificationConfig.getValue()[type];
    }

    changeTime(timeType: TimeType, val: number): void {
        let hour = this.time[timeType].hour;
        let minute = this.time[timeType].minute;
        const time = hour * 60 + minute + val;

        if (time < 0 || time >= 1440) {
            return;
        }

        this.time[timeType].hour = Math.floor(time / 60);
        this.time[timeType].minute = time % 60;

        this.diffHours();
    }

    changeSliderValue(timeType: TimeType, eventValue: number): void {
        let hour = this.time[timeType].hour;
        let minute = this.time[timeType].minute;
        const lastTime = hour * 60 + minute;
        return this.changeTime(timeType, eventValue - lastTime);
    }

    getTime(timeType: TimeType) {
        return (this.time[timeType].hour * 60) + this.time[timeType].minute;
    }

    validateEmail = function (email: string): boolean {
        if (email === '') return true; // The empty email is valid for notifications
        const regex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return regex.test(email);
    }

    changeNotificationEmail(): void {
        const validEmail = this.validateEmail(this.newNotificationEmail);

        if (!validEmail) {
            this.appAlert.openAlertDialog({
                title: this.subtitles.setSubtitle(1639),
                message: this.subtitles.setSubtitle(1342),
                okButtonText: this.subtitles.setSubtitle(1342),
            })
            return;
        }

        const udid = this.settings.getSelectedUdid();
        const baseUrl = `/api/v1/users/${this.user.user_id}/modules/${udid}`;
        const data = {email: this.newNotificationEmail};

        this.http.put<{status: string}>(`${baseUrl}/notifications/email`, data)
            .subscribe(response => {
                if (response.status === 'success') {
                    if (this.parameters.modules.length) {
                        const moduleIndex = this.parameters.modules.findIndex(module => module.udid === udid);
                        this.parameters.modules[moduleIndex].email = this.newNotificationEmail;
                    }
                    this.settings.modules = _.cloneDeep(this.parameters.modules);
                    if (this.settings.selectedModule) {
                        this.settings.selectedModule.email = this.newNotificationEmail;
                    }
                    this.newNotificationEmail = '';
                    this.appAlert.openAlertDialog({
                        title: this.subtitles.setSubtitle(1639),
                        message: this.subtitles.setSubtitle(2973),
                        okButtonText: this.subtitles.setSubtitle(475),
                    })
                } else {
                    this.appAlert.openAlertDialog({
                        title: this.subtitles.setSubtitle(1639),
                        message: this.subtitles.setSubtitle(1941),
                        okButtonText: this.subtitles.setSubtitle(1136),
                    })
                }
            });
    }

    diffHours(): void {
        let dt1 = '';
        dt1 += this.time.from.hour < 10 ? '0' : '';
        dt1 += this.time.from.hour;
        dt1 += ':';
        dt1 += this.time.from.minute < 10 ? '0' : '';
        dt1 += this.time.from.minute;

        let dt2 = '';
        dt2 += this.time.to.hour < 10 ? '0' : '';
        dt2 += this.time.to.hour;
        dt2 += ':';
        dt2 += this.time.to.minute < 10 ? '0' : '';
        dt2 += this.time.to.minute;

        let data1 = new Date('October 13, 2018 ' + dt1);
        let data2 = new Date('October 13, 2018 ' + dt2);

        if (data2.getTime() < data1.getTime()) {
            data2 = new Date('October 14, 2018 ' + dt2);
        }

        let msec = data2.getTime() - data1.getTime();
        let mins = Math.floor(msec / 60000);

        this.hoursDifference = Math.floor(mins / 60);
        this.minutesDifference = mins % 60;
    }
}
